import { zodResolver } from "@hookform/resolvers/zod";
import type { UserProfile, UserProfileRead } from "epsm-api-types/models";
import { useState } from "react";
import { VisuallyHidden } from "react-aria";
import { useForm } from "react-hook-form";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { getDirtyValues } from "@/components/user/lib/helpers/getDirtyValues";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { EditIcon } from "@/shared/assets/icons/ui/EditIcon";
import { PhoneNumberForm } from "@/shared/components/PhoneNumberForm";
import { formSchema } from "../../../config";

interface UpdateAccountFormProps {
	userData: Partial<UserProfile & UserProfileRead>;
	updateUserData: (data: Partial<UserProfile>) => Promise<void>;
	isLoadingUpdate: boolean;
}

export const UpdateAccountForm = ({
	userData,
	updateUserData,
	isLoadingUpdate,
}: UpdateAccountFormProps) => {
	const [openModal, setOpenModal] = useState(false);

	const form = useForm({
		resolver: zodResolver(formSchema),

		values: {
			firstName: userData?.firstName || "",
			lastName: userData?.lastName || "",
			email: userData?.email || "",
		},
		mode: "onBlur",
	});

	const {
		control,
		formState: { isValid, isDirty, dirtyFields },
		handleSubmit,
	} = form;

	const onSubmit = async (formValues: Partial<UserProfile>) => {
		try {
			const dirtyValues = getDirtyValues(dirtyFields, formValues);

			if (!Object.keys(dirtyValues).length) return;

			await updateUserData(dirtyValues);
		} catch (error) {
			ErrorHandler.logError(error);
		}
	};

	return (
		<div>
			<Form {...form}>
				<form
					className="flex flex-col gap-8"
					onSubmit={handleSubmit(onSubmit)}
					data-testid="update-account-form"
				>
					<FormField
						control={control}
						name="firstName"
						render={({ field }) => (
							<FormItem className="space-y-0 flex-col">
								<div className="flex">
									<FormLabel className="min-w-[100px] text-[var(--light-gray)]">
										First Name
									</FormLabel>
									<FormControl className="w-full max-w-[180px]">
										<Input placeholder="Name" className="text-lg" {...field} />
									</FormControl>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={control}
						name="lastName"
						render={({ field }) => (
							<FormItem className="space-y-0 flex-col">
								<div className="flex">
									<FormLabel className="min-w-[100px] text-[var(--light-gray)]">
										Last Name
									</FormLabel>
									<FormControl className="w-full max-w-[180px]">
										<Input
											placeholder="Last Name"
											className="text-lg"
											{...field}
										/>
									</FormControl>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={control}
						name="email"
						disabled={true}
						render={({ field }) => (
							<FormItem className="space-y-0 flex-col">
								<div className="flex">
									<FormLabel className="min-w-[100px] text-[var(--light-gray)]">
										Email
									</FormLabel>
									<FormControl className="w-full max-w-[180px]">
										<Input placeholder="Email" className="text-lg" {...field} />
									</FormControl>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="flex items-center gap-4">
						<FormField
							control={control}
							name="phone"
							render={() => (
								<FormItem className="h-fit">
									<div className="flex">
										<FormLabel className="min-w-[100px] text-[var(--light-gray)]">
											Phone
										</FormLabel>
										<FormControl className="max-w-40">
											<Input
												placeholder="Phone"
												className="text-lg"
												value={userData?.phone || "N/A"}
												disabled={true}
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							className="h-fit bg-transparent hover:bg-transparent fill-white hover:fill-gold transition-colors duration-200 p-1"
							onClick={() => {
								setOpenModal((prev) => !prev);
							}}
							data-testid="edit-phone"
						>
							<EditIcon />
						</Button>
					</div>

					<Button
						disabled={!isValid || !isDirty || isLoadingUpdate}
						type="submit"
						variant="dropdown"
						className="text-[16px] w-fit"
						data-testid="update-account-form-submit"
					>
						Update
					</Button>

					{isLoadingUpdate && <Spinner />}
				</form>
			</Form>

			<Dialog open={openModal} onOpenChange={setOpenModal} data-testid="modal">
				<DialogContent className="gap-0 p-0">
					<VisuallyHidden>
						<DialogTitle>Update Phone Number</DialogTitle>
					</VisuallyHidden>

					<PhoneNumberForm
						className="md:w-full border-none p-0"
						showTitle={false}
						onClose={(phone) => {
							if (phone) {
								userData.phone = phone; // optimistic update
							}
							setOpenModal(false);
						}}
						onSuccess={() => setOpenModal(false)}
					/>
				</DialogContent>
			</Dialog>
		</div>
	);
};
